import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined"
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined"
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded"
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined"
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import MainLayout from "../../layouts/MainLayout"

import {
  getBusinessLabel,
  getDashboardConfig,
} from "../../config/appConfig"

import {
  getWarehouseDashboard,
} from "../../services/warehouseService"


/* =========================================================
   CONFIGURATION
   ========================================================= */


const DASHBOARD_SURFACE = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
  backgroundColor: "background.paper",
}

const DASHBOARD_FILTER_SURFACE = {
  ...DASHBOARD_SURFACE,
  p: { xs: 1.5, sm: 2 },
  mb: 2.5,
}

const DASHBOARD_SECTION_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
}

const MAIN_COMPANY = getBusinessLabel(
  "mainCompany",
  "Entreprise principale"
)

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOTS = getBusinessLabel(
  "relayDepots",
  "Structures secondaires"
)

const SHOPS = getBusinessLabel(
  "shops",
  "Shops"
)

const FIELD_AGENTS = getBusinessLabel(
  "fieldAgents",
  "Agents commerciaux"
)

const WAREHOUSE = getBusinessLabel(
  "warehouse",
  "Entrepôt"
)

const WAREHOUSES = getBusinessLabel(
  "warehouses",
  "Entrepôts"
)

const WAREHOUSE_DASHBOARD_CONFIG =
  getDashboardConfig("warehouses")

const WAREHOUSE_DASHBOARD_TITLE =
  WAREHOUSE_DASHBOARD_CONFIG?.label ||
  `Dashboard ${WAREHOUSES.toLowerCase()}`

const WAREHOUSE_DASHBOARD_DESCRIPTION =
  WAREHOUSE_DASHBOARD_CONFIG?.description ||
  `Pilotage des stocks, achats, réceptions, mouvements et valorisation par ${WAREHOUSE.toLowerCase()}.`

const PAGE_SIZE = 5


/* =========================================================
   LABELS
   ========================================================= */

const warehouseTypeLabels = {
  RAW_MATERIALS: "Matières premières",
  FINISHED_GOODS: "Produits finis",
  MIXED: "Stock mixte",
  TRANSIT: "Transit",
}

const warehouseTypeColors = {
  RAW_MATERIALS: "warning",
  FINISHED_GOODS: "success",
  MIXED: "default",
  TRANSIT: "info",
}

const movementTypeLabels = {
  opening_stock: "Stock initial",
  stock_in: "Entrée manuelle",
  production_in: "Entrée production",
  production_output: "Production",
  production_consumption: "Consommation production",
  purchase_in: "Entrée achat",
  purchase_receipt: "Réception fournisseur",
  transfer_in: "Entrée transfert",
  transfer_out: "Sortie transfert",
  stock_order_in: "Entrée commande stock",
  stock_order_out: "Sortie commande stock",
  sale_out: "Sortie vente",
  audit_correction_in: "Correction audit +",
  audit_correction_out: "Correction audit -",
  adjustment_in: "Ajustement +",
  adjustment_out: "Ajustement -",

  PRODUCTION_OUTPUT: "Production",
  PRODUCTION_CONSUMPTION: "Consommation production",
}

const costSourceLabels = {
  SUPPLIER_INVOICE: "Facture fournisseur",
  STOCK_MOVEMENT: "Mouvement stock",
  CATALOG: "Catalogue",
  NO_COST: "Non valorisé",
}


/* =========================================================
   HELPERS
   ========================================================= */

function toNumber(value) {
  const number = Number(value ?? 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatMoney(
  value,
  currency = "USD"
) {
  return `${formatAmount(value)} ${currency}`
}


function formatQuantity(
  value,
  unit = ""
) {
  const formatted = toNumber(
    value
  ).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

  return unit
    ? `${formatted} ${unit}`
    : formatted
}


function formatDateTime(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}


/* =========================================================
   NORMALISATION ENTREPÔT
   ========================================================= */

function normalizeWarehouse(
  warehouse = {}
) {
  return {
    ...warehouse,

    id:
      warehouse.id ??
      warehouse.warehouse_id ??
      warehouse.value ??
      null,

    code:
      warehouse.code ??
      warehouse.warehouse_code ??
      "",

    name:
      warehouse.name ??
      warehouse.warehouse_name ??
      "",

    warehouse_type:
      warehouse.warehouse_type ??
      warehouse.type ??
      "",

    city:
      warehouse.city ??
      warehouse.warehouse_city ??
      "",

    province:
      warehouse.province ??
      warehouse.warehouse_province ??
      "",

    products:
      toNumber(
        warehouse.products ??
          warehouse.product_count ??
          warehouse.products_count ??
          warehouse.distinct_products
      ),

    quantity:
      toNumber(
        warehouse.quantity ??
          warehouse.total_quantity ??
          warehouse.stock_quantity
      ),

    purchase_value:
      toNumber(
        warehouse.purchase_value ??
          warehouse.acquisition_value ??
          warehouse.stock_value ??
          warehouse.stock_cost
      ),

    sale_value:
      toNumber(
        warehouse.sale_value ??
          warehouse.sales_value ??
          warehouse.stock_sale_value
      ),

    potential_margin:
      toNumber(
        warehouse.potential_margin ??
          warehouse.margin ??
          warehouse.margin_value
      ),
  }
}


/* =========================================================
   NORMALISATION PRODUIT
   ========================================================= */

function normalizeProduct(
  product = {}
) {
  return {
    ...product,

    product_id:
      product.product_id ??
      product.id ??
      null,

    product_code:
      product.product_code ??
      product.code ??
      "",

    product_name:
      product.product_name ??
      product.name ??
      "",

    product_unit:
      product.product_unit ??
      product.unit ??
      "",

    warehouse_id:
      product.warehouse_id ??
      product.stock_warehouse_id ??
      null,

    warehouse_code:
      product.warehouse_code ??
      "",

    warehouse_name:
      product.warehouse_name ??
      "",

    warehouse_type:
      product.warehouse_type ??
      "",

    quantity:
      toNumber(
        product.quantity ??
          product.available_quantity ??
          product.stock_quantity
      ),

    effective_unit_cost:
      toNumber(
        product.effective_unit_cost ??
          product.unit_cost ??
          product.purchase_price
      ),

    purchase_value:
      toNumber(
        product.purchase_value ??
          product.stock_value ??
          product.total_cost
      ),

    sale_value:
      toNumber(
        product.sale_value ??
          product.total_sale_value
      ),
  }
}


/* =========================================================
   TYPE ENTREPÔT
   ========================================================= */

function getWarehouseTypeLabel(
  type,
  backendLabels = {}
) {
  return (
    backendLabels?.[type] ||
    warehouseTypeLabels[type] ||
    type ||
    "Non classé"
  )
}


/* =========================================================
   KPI CARD
   ========================================================= */

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  valueColor = "text.primary",
}) {
  return (
    <Card
      elevation={0}
      sx={{
        ...DASHBOARD_SURFACE,
        height: "100%",
        minHeight: 116,
        background:
          "linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.075)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.2,
          "&:last-child": {
            pb: 2.2,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "primary.50",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={700}
            >
              {title}
            </Typography>

            <Typography
              variant="h5"
              fontWeight={900}
              color={valueColor}
              mt={0.35}
              sx={{
                wordBreak: "break-word",
              }}
            >
              {value}
            </Typography>

            {subtitle ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
                lineHeight={1.4}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


/* =========================================================
   EMPTY TABLE
   ========================================================= */

function EmptyRow({
  colSpan,
  message,
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 4,
          textAlign: "center",
        }}
      >
        <Typography
          color="text.secondary"
        >
          {message}
        </Typography>
      </TableCell>
    </TableRow>
  )
}


/* =========================================================
   SECTION HEADER
   ========================================================= */

function SectionHeader({
  title,
  subtitle,
}) {
  return (
    <Box
      sx={{
        px: {
          xs: 1.6,
          sm: 2.2,
        },
        py: 1.8,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="h6"
        fontWeight={900}
      >
        {title}
      </Typography>

      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          mt={0.3}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )
}


/* =========================================================
   PAGINATION PROFESSIONNELLE
   ========================================================= */

function TablePager({
  page,
  totalItems,
  pageSize = PAGE_SIZE,
  onPageChange,
}) {
  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / pageSize)
  )

  const currentPage = Math.min(
    page,
    totalPages - 1
  )

  const start =
    totalItems === 0
      ? 0
      : currentPage * pageSize + 1

  const end = Math.min(
    (currentPage + 1) * pageSize,
    totalItems
  )

  return (
    <Box
      sx={{
        minHeight: 58,
        px: {
          xs: 1.25,
          sm: 2,
        },
        py: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "#fafbfd",
        display: "flex",
        alignItems: "center",
        justifyContent: {
          xs: "center",
          sm: "flex-end",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={{
          xs: 0.5,
          sm: 1,
        }}
        alignItems="center"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={700}
          sx={{
            mr: {
              xs: 0.25,
              sm: 1,
            },
            whiteSpace: "nowrap",
          }}
        >
          {totalItems === 0
            ? "0 résultat"
            : `${start}–${end} sur ${totalItems}`}
        </Typography>

        <Tooltip title="Page précédente">
          <span>
            <IconButton
              size="small"
              disabled={
                totalItems === 0 ||
                currentPage === 0
              }
              onClick={() =>
                onPageChange(
                  Math.max(
                    0,
                    currentPage - 1
                  )
                )
              }
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Box
          sx={{
            minWidth: {
              xs: 58,
              sm: 72,
            },
            px: 1,
            py: 0.65,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={900}
          >
            {totalItems === 0
              ? "0 / 0"
              : `${currentPage + 1} / ${totalPages}`}
          </Typography>
        </Box>

        <Tooltip title="Page suivante">
          <span>
            <IconButton
              size="small"
              disabled={
                totalItems === 0 ||
                currentPage >=
                  totalPages - 1
              }
              onClick={() =>
                onPageChange(
                  Math.min(
                    totalPages - 1,
                    currentPage + 1
                  )
                )
              }
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}


/* =========================================================
   PAGE
   ========================================================= */

export default function WarehouseDashboardPage() {
  const [dashboard, setDashboard] =
    useState({
      summary: {},
      movement_stats: {},
      receipt_stats: {},
      warehouses: [],
      products: [],
      low_stock: [],
      recent_movements: [],
      recent_receipts: [],
      warehouse_options: [],
      warehouse_type_labels: {},
    })

  const [
    warehouseFilter,
    setWarehouseFilter,
  ] = useState("")

  const [
    productsPage,
    setProductsPage,
  ] = useState(0)

  const [
    receiptsPage,
    setReceiptsPage,
  ] = useState(0)

  const [
    movementsPage,
    setMovementsPage,
  ] = useState(0)

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState("")


  /* =======================================================
     LOAD
     ======================================================= */

  const loadData = async (
    warehouseId = warehouseFilter
  ) => {
    try {
      setLoading(true)
      setError("")

      const data =
        await getWarehouseDashboard({
          warehouse_id:
            warehouseId || "",
        })

      const rawWarehouses =
        Array.isArray(data?.warehouses)
          ? data.warehouses
          : []

      const normalizedWarehouses =
        rawWarehouses.map(
          normalizeWarehouse
        )

      const rawOptions =
        Array.isArray(
          data?.warehouse_options
        )
          ? data.warehouse_options
          : []

      let normalizedOptions =
        rawOptions.map(
          normalizeWarehouse
        )

      if (
        normalizedOptions.length === 0
      ) {
        normalizedOptions =
          normalizedWarehouses
      }

      const rawProducts =
        Array.isArray(data?.products)
          ? data.products
          : []

      const normalizedProducts =
        rawProducts.map(
          normalizeProduct
        )

      setDashboard({
        summary:
          data?.summary || {},

        movement_stats:
          data?.movement_stats || {},

        receipt_stats:
          data?.receipt_stats || {},

        warehouses:
          normalizedWarehouses,

        products:
          normalizedProducts,

        low_stock:
          Array.isArray(data?.low_stock)
            ? data.low_stock
            : [],

        recent_movements:
          Array.isArray(
            data?.recent_movements
          )
            ? data.recent_movements
            : [],

        recent_receipts:
          Array.isArray(
            data?.recent_receipts
          )
            ? data.recent_receipts
            : [],

        warehouse_options:
          normalizedOptions,

        warehouse_type_labels:
          data?.warehouse_type_labels ||
          {},
      })

      /*
       * À chaque actualisation ou changement
       * d'entrepôt, retour à la première page.
       */
      setProductsPage(0)
      setReceiptsPage(0)
      setMovementsPage(0)
    } catch (err) {
      console.error(
        "Warehouse dashboard error:",
        err
      )

      setError(
        err?.message ||
          `Impossible de charger le dashboard ${WAREHOUSES}.`
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadData("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  /* =======================================================
     DATA
     ======================================================= */

  const summary =
    dashboard.summary || {}

  const movementStats =
    dashboard.movement_stats || {}

  const receiptStats =
    dashboard.receipt_stats || {}


  /* =======================================================
     PAGINATION
     ======================================================= */

  const paginatedProducts =
    useMemo(() => {
      const start =
        productsPage * PAGE_SIZE

      return dashboard.products.slice(
        start,
        start + PAGE_SIZE
      )
    }, [
      dashboard.products,
      productsPage,
    ])


  const paginatedReceipts =
    useMemo(() => {
      const start =
        receiptsPage * PAGE_SIZE

      return dashboard.recent_receipts.slice(
        start,
        start + PAGE_SIZE
      )
    }, [
      dashboard.recent_receipts,
      receiptsPage,
    ])


  const paginatedMovements =
    useMemo(() => {
      const start =
        movementsPage * PAGE_SIZE

      return dashboard.recent_movements.slice(
        start,
        start + PAGE_SIZE
      )
    }, [
      dashboard.recent_movements,
      movementsPage,
    ])


  /* =======================================================
     SELECTED WAREHOUSE
     ======================================================= */

  const selectedWarehouse =
    useMemo(() => {
      if (!warehouseFilter) {
        return null
      }

      return (
        dashboard.warehouse_options.find(
          (warehouse) =>
            String(warehouse.id) ===
            String(warehouseFilter)
        ) || null
      )
    }, [
      dashboard.warehouse_options,
      warehouseFilter,
    ])


  /* =======================================================
     FALLBACK METRICS
     ======================================================= */

  const calculatedMetrics =
    useMemo(() => {
      const products =
        dashboard.products || []

      const rawProducts =
        products.filter(
          (product) =>
            product.warehouse_type ===
              "RAW_MATERIALS" ||
            String(
              product.product_type || ""
            ).toUpperCase() ===
              "RAW_MATERIAL"
        )

      const finishedProducts =
        products.filter(
          (product) =>
            product.warehouse_type ===
              "FINISHED_GOODS" ||
            String(
              product.product_type || ""
            ).toUpperCase() ===
              "FINISHED_GOOD"
        )

      return {
        totalQuantity:
          products.reduce(
            (sum, product) =>
              sum +
              toNumber(
                product.quantity
              ),
            0
          ),

        rawQuantity:
          rawProducts.reduce(
            (sum, product) =>
              sum +
              toNumber(
                product.quantity
              ),
            0
          ),

        finishedQuantity:
          finishedProducts.reduce(
            (sum, product) =>
              sum +
              toNumber(
                product.quantity
              ),
            0
          ),

        rawValue:
          rawProducts.reduce(
            (sum, product) =>
              sum +
              toNumber(
                product.purchase_value
              ),
            0
          ),

        finishedValue:
          finishedProducts.reduce(
            (sum, product) =>
              sum +
              toNumber(
                product.purchase_value
              ),
            0
          ),
      }
    }, [dashboard.products])




  /* =======================================================
     GRAPHIQUES
     ======================================================= */

  const warehouseQuantityChartData =
    useMemo(() => {
      return (dashboard.warehouses || [])
        .map((warehouse) => ({
          /*
           * Le graphique affiche d'abord le vrai nom
           * de l'entrepôt au lieu du code technique WH-xxx.
           */
          name:
            warehouse.name ||
            warehouse.code ||
            "Entrepôt",

          /*
           * Le tooltip conserve le code pour la traçabilité,
           * sans encombrer l'axe du graphique.
           */
          full_name:
            warehouse.name &&
            warehouse.code
              ? `${warehouse.name} (${warehouse.code})`
              : warehouse.name ||
                warehouse.code ||
                "Entrepôt",

          quantity: toNumber(
            warehouse.quantity
          ),
        }))
        .filter(
          (item) =>
            item.quantity > 0
        )
    }, [dashboard.warehouses])


  const stockValueChartData =
    useMemo(() => {
      const groups = {}

      ;(dashboard.products || []).forEach(
        (product) => {
          const type =
            product.warehouse_type ||
            "OTHER"

          const label =
            getWarehouseTypeLabel(
              type,
              dashboard.warehouse_type_labels
            )

          if (!groups[type]) {
            groups[type] = {
              type,
              name: label,
              value: 0,
            }
          }

          groups[type].value +=
            toNumber(
              product.purchase_value
            )
        }
      )

      return Object.values(
        groups
      ).filter(
        (item) =>
          item.value > 0
      )
    }, [
      dashboard.products,
      dashboard.warehouse_type_labels,
    ])


  const topStockProductsChartData =
    useMemo(() => {
      return [...(dashboard.products || [])]
        .sort(
          (a, b) =>
            toNumber(b.quantity) -
            toNumber(a.quantity)
        )
        .slice(0, 5)
        .map((product) => ({
          /*
           * Le graphique affiche le vrai nom métier
           * du produit, pas son code technique PRODxxx.
           */
          name:
            product.product_name ||
            product.product_code ||
            "Produit",

          /*
           * Le tooltip garde aussi le code produit
           * afin de conserver la traçabilité.
           */
          full_name:
            product.product_name &&
            product.product_code
              ? `${product.product_name} (${product.product_code})`
              : product.product_name ||
                product.product_code ||
                "Produit",

          quantity: toNumber(
            product.quantity
          ),
        }))
    }, [dashboard.products])


  const pieColors = [
    "#ed6c02",
    "#2e7d32",
    "#1976d2",
    "#7b1fa2",
    "#0288d1",
    "#64748b",
  ]


  /* =======================================================
     DISPLAY METRICS
     ======================================================= */

  const totalQuantity =
    toNumber(
      summary.total_quantity
    ) > 0
      ? toNumber(
          summary.total_quantity
        )
      : calculatedMetrics.totalQuantity

  const rawMaterialQuantity =
    toNumber(
      summary.raw_material_quantity
    ) > 0
      ? toNumber(
          summary.raw_material_quantity
        )
      : calculatedMetrics.rawQuantity

  const finishedGoodsQuantity =
    toNumber(
      summary.finished_goods_quantity
    ) > 0
      ? toNumber(
          summary.finished_goods_quantity
        )
      : calculatedMetrics.finishedQuantity

  const rawMaterialValue =
    toNumber(
      summary.raw_material_value
    ) > 0
      ? toNumber(
          summary.raw_material_value
        )
      : calculatedMetrics.rawValue

  const finishedGoodsValue =
    toNumber(
      summary.finished_goods_purchase_value
    ) > 0
      ? toNumber(
          summary.finished_goods_purchase_value
        )
      : calculatedMetrics.finishedValue


  /* =======================================================
     FILTER
     ======================================================= */

  const handleWarehouseFilter =
    async (value) => {
      setWarehouseFilter(value)

      await loadData(value)
    }


  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >

        {/* =================================================
            HEADER
            ================================================= */}

        <Box sx={{ mb: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", fontWeight: 900, letterSpacing: 1.1 }}
          >
            PILOTAGE
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-end" }}
            spacing={1.5}
          >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <WarehouseOutlinedIcon
                color="primary"
                sx={{
                  fontSize: {
                    xs: 30,
                    md: 34,
                  },
                }}
              />

              <Typography
                variant="h4"
                fontWeight={900}
                sx={{
                  fontSize: {
                    xs: "1.65rem",
                    sm: "2rem",
                    md: "2.125rem",
                  },
                }}
              >
                {WAREHOUSE_DASHBOARD_TITLE}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              {
                WAREHOUSE_DASHBOARD_DESCRIPTION
              }
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={
              <RefreshRoundedIcon />
            }
            onClick={() =>
              loadData()
            }
            disabled={loading}
            sx={{
              minHeight: 44,
              px: 2.5,
              borderRadius: 2,
              fontWeight: 800,
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            Actualiser
          </Button>
          </Stack>
        </Box>


        {/* =================================================
            INFO
            ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2.5,
          }}
        >
          Vue centrale des stocks de{" "}
          {MAIN_COMPANY}. Les matières
          premières sont valorisées selon
          leur coût réel fournisseur lorsque
          celui-ci est disponible. Les
          produits finis peuvent ensuite
          alimenter les{" "}
          {SHOPS.toLowerCase()},{" "}
          {MAIN_DISTRIBUTOR.toLowerCase()},
          {" "}
          {RELAY_DEPOTS.toLowerCase()} et{" "}
          {FIELD_AGENTS.toLowerCase()}.
        </Alert>


        {error ? (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2.5,
            }}
          >
            {error}
          </Alert>
        ) : null}


        {/* =================================================
            FILTER PANEL
            ================================================= */}

        <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
          >
            <TextField
              select
              fullWidth
              label="Analyser un entrepôt"
              value={
                warehouseFilter
              }
              onChange={(event) =>
                handleWarehouseFilter(
                  event.target.value
                )
              }
              helperText={
                warehouseFilter
                  ? "Les indicateurs affichent uniquement l'entrepôt sélectionné."
                  : "Vue consolidée de tous les entrepôts."
              }
            >
              <MenuItem value="">
                Tous les entrepôts
              </MenuItem>

              {dashboard.warehouse_options.map(
                (warehouse) => (
                  <MenuItem
                    key={
                      warehouse.id ??
                      `${warehouse.code}-${warehouse.name}`
                    }
                    value={String(
                      warehouse.id
                    )}
                  >
                    {warehouse.code
                      ? `${warehouse.code} - `
                      : ""}
                    {warehouse.name ||
                      "Entrepôt"}

                    {warehouse.warehouse_type
                      ? ` • ${getWarehouseTypeLabel(
                          warehouse.warehouse_type,
                          dashboard.warehouse_type_labels
                        )}`
                      : ""}
                  </MenuItem>
                )
              )}
            </TextField>

            <Button
              variant="outlined"
              startIcon={
                <RefreshRoundedIcon />
              }
              onClick={() =>
                loadData(
                  warehouseFilter
                )
              }
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  md: 150,
                },
                minHeight: 56,
                borderRadius: 2,
                fontWeight: 800,
              }}
            >
              Actualiser
            </Button>
          </Stack>


          {selectedWarehouse ? (
            <>
              <Divider
                sx={{
                  my: 2,
                }}
              />

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1}
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  fontWeight={900}
                >
                  Analyse :
                </Typography>

                <Chip
                  color="primary"
                  label={`${
                    selectedWarehouse.code ||
                    ""
                  } ${
                    selectedWarehouse.name
                      ? `- ${selectedWarehouse.name}`
                      : ""
                  }`}
                />

                <Chip
                  variant="outlined"
                  color={
                    warehouseTypeColors[
                      selectedWarehouse
                        .warehouse_type
                    ] || "default"
                  }
                  label={getWarehouseTypeLabel(
                    selectedWarehouse.warehouse_type,
                    dashboard.warehouse_type_labels
                  )}
                />

                {selectedWarehouse.city ? (
                  <Chip
                    variant="outlined"
                    label={`${selectedWarehouse.city}${
                      selectedWarehouse.province
                        ? ` / ${selectedWarehouse.province}`
                        : ""
                    }`}
                  />
                ) : null}
              </Stack>
            </>
          ) : null}
        </Paper>


        {/* =================================================
            LOADING
            ================================================= */}

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              minHeight: 260,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              spacing={1.5}
              alignItems="center"
            >
              <CircularProgress />

              <Typography
                fontWeight={700}
              >
                Chargement du dashboard...
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <>
            {/* =============================================
                KPIs
                ============================================= */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <KpiCard
                title="Entrepôts actifs"
                value={toNumber(
                  summary.active_warehouses ??
                    dashboard.warehouse_options.length
                )}
                subtitle={`${toNumber(
                  summary.warehouses_with_stock
                )} avec stock`}
                icon={
                  <WarehouseOutlinedIcon />
                }
              />

              <KpiCard
                title="Quantité totale"
                value={formatQuantity(
                  totalQuantity
                )}
                subtitle={`${toNumber(
                  summary.distinct_products ??
                    dashboard.products.length
                )} produit(s) distinct(s)`}
                icon={
                  <Inventory2OutlinedIcon />
                }
              />

              <KpiCard
                title="Matières premières"
                value={formatQuantity(
                  rawMaterialQuantity
                )}
                subtitle={`Valeur : ${formatMoney(
                  rawMaterialValue
                )}`}
                icon={
                  <FactoryOutlinedIcon />
                }
              />

              <KpiCard
                title="Produits finis"
                value={formatQuantity(
                  finishedGoodsQuantity
                )}
                subtitle={`Valeur acquisition : ${formatMoney(
                  finishedGoodsValue
                )}`}
                icon={
                  <CategoryOutlinedIcon />
                }
              />

              <KpiCard
                title="Valeur d'acquisition"
                value={formatMoney(
                  summary.purchase_value
                )}
                subtitle="Coût réel du stock disponible"
                icon={
                  <PaymentsOutlinedIcon />
                }
              />

              <KpiCard
                title="Valeur de vente"
                value={formatMoney(
                  summary.sale_value
                )}
                subtitle="Valeur potentielle des produits vendables"
                icon={
                  <TrendingUpRoundedIcon />
                }
              />

              <KpiCard
                title="Réceptions mises en stock"
                value={toNumber(
                  receiptStats.receipts_count
                )}
                subtitle={`${formatQuantity(
                  receiptStats.received_quantity
                )} réceptionnée`}
                icon={
                  <LocalShippingOutlinedIcon />
                }
              />

              <KpiCard
                title="Stock faible"
                value={toNumber(
                  summary.low_stock_count
                )}
                subtitle="Articles nécessitant une attention"
                icon={
                  <WarningAmberRoundedIcon />
                }
                valueColor={
                  toNumber(
                    summary.low_stock_count
                  ) > 0
                    ? "warning.main"
                    : "success.main"
                }
              />
            </Box>


            {/* =============================================
                MOUVEMENTS KPI
                ============================================= */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <KpiCard
                title="Entrées en stock"
                value={formatQuantity(
                  movementStats.in_quantity
                )}
                subtitle={`${toNumber(
                  movementStats.in_count
                )} mouvement(s) entrant(s)`}
                icon={
                  <LocalShippingOutlinedIcon />
                }
              />

              <KpiCard
                title="Sorties de stock"
                value={formatQuantity(
                  movementStats.out_quantity
                )}
                subtitle={`${toNumber(
                  movementStats.out_count
                )} mouvement(s) sortant(s)`}
                icon={
                  <SwapHorizRoundedIcon />
                }
              />
            </Box>



            {/* =============================================
                GRAPHIQUES
                ============================================= */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                mb: 2,
              }}
            >
              {/* ===========================================
                  HISTOGRAMME — STOCK PAR ENTREPÔT
                  =========================================== */}

              <Paper
                elevation={0}
                sx={{
                  ...DASHBOARD_SURFACE,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <SectionHeader
                  title="Stock par entrepôt"
                  subtitle="Comparaison des quantités actuellement disponibles par entrepôt."
                />

                <Box
                  sx={{
                    width: "100%",
                    height: {
                      xs: 290,
                      sm: 320,
                    },
                    p: {
                      xs: 1,
                      sm: 2,
                    },
                  }}
                >
                  {warehouseQuantityChartData.length ===
                  0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Aucune donnée disponible.
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <BarChart
                        data={
                          warehouseQuantityChartData
                        }
                        margin={{
                          top: 10,
                          right: 10,
                          left: 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                        />

                        <XAxis
                          dataKey="name"
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          tick={{
                            fontSize: 12,
                          }}
                        />

                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          width={65}
                        />

                        <RechartsTooltip
                          formatter={(value) => [
                            formatQuantity(
                              value
                            ),
                            "Quantité",
                          ]}
                          labelFormatter={(
                            label,
                            payload
                          ) =>
                            payload?.[0]
                              ?.payload
                              ?.full_name ||
                            label
                          }
                        />

                        <Bar
                          dataKey="quantity"
                          fill="#1976d2"
                          radius={[
                            8,
                            8,
                            0,
                            0,
                          ]}
                          maxBarSize={72}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Paper>


              {/* ===========================================
                  DONUT — VALEUR DU STOCK
                  =========================================== */}

              <Paper
                elevation={0}
                sx={{
                  ...DASHBOARD_SURFACE,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <SectionHeader
                  title="Répartition de la valeur du stock"
                  subtitle="Part de la valeur d'acquisition selon le type de stock."
                />

                <Box
                  sx={{
                    width: "100%",
                    height: {
                      xs: 290,
                      sm: 320,
                    },
                    p: {
                      xs: 1,
                      sm: 2,
                    },
                  }}
                >
                  {stockValueChartData.length ===
                  0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Aucune valeur disponible.
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <PieChart>
                        <Pie
                          data={
                            stockValueChartData
                          }
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="45%"
                          innerRadius={62}
                          outerRadius={94}
                          paddingAngle={2}
                          stroke="none"
                        >
                          {stockValueChartData.map(
                            (entry, index) => (
                              <Cell
                                key={`${entry.type}-${index}`}
                                fill={
                                  pieColors[
                                    index %
                                      pieColors.length
                                  ]
                                }
                              />
                            )
                          )}
                        </Pie>

                        <RechartsTooltip
                          formatter={(value) => [
                            formatMoney(value),
                            "Valeur",
                          ]}
                        />

                        <Legend
                          verticalAlign="bottom"
                          height={42}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Paper>
            </Box>


            {/* =============================================
                TOP 5 PRODUITS EN STOCK
                ============================================= */}

            <Paper
              elevation={0}
              sx={{
                ...DASHBOARD_SURFACE,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <SectionHeader
                title="Top 5 produits en stock"
                subtitle="Produits présentant les quantités physiques les plus élevées."
              />

              <Box
                sx={{
                  width: "100%",
                  height: {
                    xs: 300,
                    sm: 330,
                  },
                  p: {
                    xs: 1,
                    sm: 2,
                  },
                }}
              >
                {topStockProductsChartData.length ===
                0 ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Aucun produit disponible.
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={
                        topStockProductsChartData
                      }
                      layout="vertical"
                      margin={{
                        top: 10,
                        right: 20,
                        left: 10,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                      />

                      <XAxis
                        type="number"
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        type="category"
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        width={150}
                        tick={{
                          fontSize: 12,
                        }}
                      />

                      <RechartsTooltip
                        formatter={(value) => [
                          formatQuantity(
                            value
                          ),
                          "Quantité",
                        ]}
                        labelFormatter={(
                          label,
                          payload
                        ) =>
                          payload?.[0]
                            ?.payload
                            ?.full_name ||
                          label
                        }
                      />

                      <Bar
                        dataKey="quantity"
                        fill="#2e7d32"
                        radius={[
                          0,
                          8,
                          8,
                          0,
                        ]}
                        maxBarSize={34}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>


            {/* =============================================
                STOCK PAR ENTREPÔT
                ============================================= */}

            <Paper
              elevation={0}
              sx={{
                ...DASHBOARD_SURFACE,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <SectionHeader
                title="Situation par entrepôt"
                subtitle="Quantités, produits et valorisation par emplacement physique."
              />

              <Box
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 1000,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Code
                      </TableCell>

                      <TableCell>
                        Entrepôt
                      </TableCell>

                      <TableCell>
                        Type
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Produits
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Quantité
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Valeur acquisition
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Valeur vente
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Marge potentielle
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dashboard.warehouses
                      .length === 0 ? (
                      <EmptyRow
                        colSpan={8}
                        message="Aucun entrepôt disponible."
                      />
                    ) : (
                      dashboard.warehouses.map(
                        (warehouse) => (
                          <TableRow
                            key={
                              warehouse.id ??
                              `${warehouse.code}-${warehouse.name}`
                            }
                            hover
                          >
                            <TableCell>
                              <Typography
                                fontWeight={900}
                              >
                                {warehouse.code ||
                                  "-"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                fontWeight={700}
                              >
                                {warehouse.name ||
                                  "-"}
                              </Typography>

                              {warehouse.city ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {
                                    warehouse.city
                                  }
                                  {warehouse.province
                                    ? ` / ${warehouse.province}`
                                    : ""}
                                </Typography>
                              ) : null}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={
                                  warehouseTypeColors[
                                    warehouse
                                      .warehouse_type
                                  ] ||
                                  "default"
                                }
                                label={getWarehouseTypeLabel(
                                  warehouse.warehouse_type,
                                  dashboard.warehouse_type_labels
                                )}
                              />
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {
                                warehouse.products
                              }
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              <Typography
                                fontWeight={800}
                              >
                                {formatQuantity(
                                  warehouse.quantity
                                )}
                              </Typography>
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {formatMoney(
                                warehouse.purchase_value
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {formatMoney(
                                warehouse.sale_value
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {formatMoney(
                                warehouse.potential_margin
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>


            {/* =============================================
                PRODUCTS + ALERTS
                ============================================= */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "1.45fr 0.55fr",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  ...DASHBOARD_SURFACE,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <SectionHeader
                  title="Produits en stock et valorisation"
                  subtitle="Quantité physique, coût réel et origine de la valorisation. Affichage de 5 produits par page."
                />

                <Box
                  sx={{
                    overflowX: "auto",
                  }}
                >
                  <Table
                    sx={{
                      minWidth: 900,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Produit
                        </TableCell>

                        <TableCell>
                          Entrepôt
                        </TableCell>

                        <TableCell>
                          Type
                        </TableCell>

                        <TableCell
                          align="right"
                        >
                          Stock
                        </TableCell>

                        <TableCell
                          align="right"
                        >
                          Coût unitaire
                        </TableCell>

                        <TableCell
                          align="right"
                        >
                          Valeur
                        </TableCell>

                        <TableCell>
                          Source coût
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedProducts
                        .length === 0 ? (
                        <EmptyRow
                          colSpan={7}
                          message="Aucun produit en stock pour cette sélection."
                        />
                      ) : (
                        paginatedProducts.map(
                          (product) => (
                            <TableRow
                              key={`${product.warehouse_id}-${product.product_id}`}
                              hover
                            >
                              <TableCell>
                                <Typography
                                  fontWeight={900}
                                >
                                  {product.product_code ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {product.product_name ||
                                    "-"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {product.warehouse_code ||
                                  "-"}
                                {product.warehouse_name
                                  ? ` - ${product.warehouse_name}`
                                  : ""}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={
                                    warehouseTypeColors[
                                      product
                                        .warehouse_type
                                    ] ||
                                    "default"
                                  }
                                  label={getWarehouseTypeLabel(
                                    product.warehouse_type,
                                    dashboard.warehouse_type_labels
                                  )}
                                />
                              </TableCell>

                              <TableCell
                                align="right"
                              >
                                <Typography
                                  fontWeight={900}
                                >
                                  {formatQuantity(
                                    product.quantity,
                                    product.product_unit
                                  )}
                                </Typography>
                              </TableCell>

                              <TableCell
                                align="right"
                              >
                                {toNumber(
                                  product.effective_unit_cost
                                ) > 0
                                  ? formatMoney(
                                      product.effective_unit_cost
                                    )
                                  : "-"}
                              </TableCell>

                              <TableCell
                                align="right"
                              >
                                {toNumber(
                                  product.purchase_value
                                ) > 0
                                  ? formatMoney(
                                      product.purchase_value
                                    )
                                  : "-"}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  color={
                                    product.cost_source ===
                                    "SUPPLIER_INVOICE"
                                      ? "success"
                                      : product.cost_source ===
                                          "NO_COST"
                                        ? "warning"
                                        : "default"
                                  }
                                  label={
                                    costSourceLabels[
                                      product.cost_source
                                    ] ||
                                    product.cost_source ||
                                    "Non renseigné"
                                  }
                                />

                                {product.invoice_code ? (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    mt={0.5}
                                  >
                                    {
                                      product.invoice_code
                                    }
                                  </Typography>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          )
                        )
                      )}
                    </TableBody>
                  </Table>
                </Box>

                <TablePager
                  page={productsPage}
                  totalItems={
                    dashboard.products.length
                  }
                  onPageChange={
                    setProductsPage
                  }
                />
              </Paper>


              <Paper
                elevation={0}
                sx={{
                  ...DASHBOARD_SURFACE,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <SectionHeader
                  title="Alertes stock"
                  subtitle="Articles avec quantité disponible faible."
                />

                <Box
                  sx={{
                    overflowX: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Produit
                        </TableCell>

                        <TableCell
                          align="right"
                        >
                          Stock
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {dashboard.low_stock
                        .length === 0 ? (
                        <EmptyRow
                          colSpan={2}
                          message="Aucune alerte de stock."
                        />
                      ) : (
                        dashboard.low_stock.map(
                          (row) => (
                            <TableRow
                              key={`${row.warehouse_id}-${row.product_id}`}
                            >
                              <TableCell>
                                <Typography
                                  fontWeight={800}
                                >
                                  {row.product_code}{" "}
                                  {row.product_name
                                    ? `- ${row.product_name}`
                                    : ""}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.warehouse_code}{" "}
                                  {row.warehouse_name
                                    ? `- ${row.warehouse_name}`
                                    : ""}
                                </Typography>
                              </TableCell>

                              <TableCell
                                align="right"
                              >
                                <Chip
                                  size="small"
                                  color={
                                    toNumber(
                                      row.quantity
                                    ) <= 0
                                      ? "error"
                                      : "warning"
                                  }
                                  label={formatQuantity(
                                    row.quantity,
                                    row.product_unit
                                  )}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        )
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Box>


            {/* =============================================
                RECEIPTS — 5 PAR PAGE
                ============================================= */}

            <Paper
              elevation={0}
              sx={{
                ...DASHBOARD_SURFACE,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <SectionHeader
                title="Dernières réceptions fournisseurs"
                subtitle="Traçabilité des achats jusqu'à la mise en stock. Affichage de 5 opérations par page."
              />

              <Box
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 1000,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Réception
                      </TableCell>

                      <TableCell>
                        Bon de commande
                      </TableCell>

                      <TableCell>
                        Fournisseur
                      </TableCell>

                      <TableCell>
                        Entrepôt
                      </TableCell>

                      <TableCell>
                        Date
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Articles
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Qté acceptée
                      </TableCell>

                      <TableCell>
                        Statut
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedReceipts.length ===
                    0 ? (
                      <EmptyRow
                        colSpan={8}
                        message="Aucune réception fournisseur pour cette sélection."
                      />
                    ) : (
                      paginatedReceipts.map(
                        (receipt) => (
                          <TableRow
                            key={receipt.id}
                            hover
                          >
                            <TableCell>
                              <Typography
                                fontWeight={900}
                              >
                                {receipt.code ||
                                  "-"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {receipt.purchase_order_code ||
                                "-"}
                            </TableCell>

                            <TableCell>
                              {receipt.supplier_name ||
                                "-"}
                            </TableCell>

                            <TableCell>
                              {receipt.warehouse_code ||
                                "-"}
                              {receipt.warehouse_name
                                ? ` - ${receipt.warehouse_name}`
                                : ""}
                            </TableCell>

                            <TableCell
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {formatDateTime(
                                receipt.receipt_date
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {toNumber(
                                receipt.item_count
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {formatQuantity(
                                receipt.accepted_quantity
                              )}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                color="success"
                                label={
                                  String(
                                    receipt.status ||
                                      ""
                                  ).toUpperCase() ===
                                  "POSTED"
                                    ? "Mise en stock"
                                    : receipt.status ||
                                      "-"
                                }
                              />
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePager
                page={receiptsPage}
                totalItems={
                  dashboard
                    .recent_receipts
                    .length
                }
                onPageChange={
                  setReceiptsPage
                }
              />
            </Paper>


            {/* =============================================
                MOVEMENTS — 5 PAR PAGE
                ============================================= */}

            <Paper
              elevation={0}
              sx={{
                ...DASHBOARD_SURFACE,
                overflow: "hidden",
              }}
            >
              <SectionHeader
                title="Derniers mouvements de stock"
                subtitle="Réceptions, transferts, ventes, production, inventaires et ajustements. Affichage de 5 opérations par page."
              />

              <Box
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 1050,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Date
                      </TableCell>

                      <TableCell>
                        Entrepôt
                      </TableCell>

                      <TableCell>
                        Produit
                      </TableCell>

                      <TableCell>
                        Mouvement
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Quantité
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Coût unitaire
                      </TableCell>

                      <TableCell>
                        Référence
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedMovements.length ===
                    0 ? (
                      <EmptyRow
                        colSpan={7}
                        message="Aucun mouvement de stock pour cette sélection."
                      />
                    ) : (
                      paginatedMovements.map(
                        (movement) => (
                          <TableRow
                            key={movement.id}
                            hover
                          >
                            <TableCell
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {formatDateTime(
                                movement.created_at
                              )}
                            </TableCell>

                            <TableCell>
                              {movement.warehouse_code ||
                                "-"}
                              {movement.warehouse_name
                                ? ` - ${movement.warehouse_name}`
                                : ""}
                            </TableCell>

                            <TableCell>
                              <Typography
                                fontWeight={800}
                              >
                                {movement.product_code ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {movement.product_name ||
                                  ""}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                color={
                                  movement.flow_direction ===
                                  "out"
                                    ? "error"
                                    : "success"
                                }
                                label={
                                  movementTypeLabels[
                                    movement.movement_type
                                  ] ||
                                  movementTypeLabels[
                                    String(
                                      movement.movement_type ||
                                        ""
                                    ).toLowerCase()
                                  ] ||
                                  movement.movement_type ||
                                  "-"
                                }
                              />
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              <Typography
                                fontWeight={800}
                                color={
                                  movement.flow_direction ===
                                  "out"
                                    ? "error.main"
                                    : "success.main"
                                }
                              >
                                {formatQuantity(
                                  movement.quantity,
                                  movement.product_unit
                                )}
                              </Typography>
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {toNumber(
                                movement.unit_cost
                              ) > 0
                                ? formatMoney(
                                    movement.unit_cost
                                  )
                                : "-"}
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                {movement.reference_type ||
                                  "-"}
                              </Typography>

                              {movement.external_reference ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {
                                    movement.external_reference
                                  }
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePager
                page={movementsPage}
                totalItems={
                  dashboard
                    .recent_movements
                    .length
                }
                onPageChange={
                  setMovementsPage
                }
              />
            </Paper>
          </>
        )}
      </Box>
    </MainLayout>
  )
}